import { useState, useCallback, useMemo, type ReactNode } from 'react';
import { Mail, Phone, Globe } from 'lucide-react';
import { FaAndroid, FaApple, FaSteam } from 'react-icons/fa';
import { webApi, influencerApi } from '@/lib/api';
import { useNewInfluencerAuth } from '@/lib/config/apiFlags';
import { regionsTree, type RegionNode } from '@/lib/constants/regions';
import { languages } from '@/lib/constants/languages';

/**
 * 원본: .old-gc-mono/apps/gc-zoku/components/pages/CampaignChecklist/
 * 캠페인 체크리스트 제출 페이지 (광고주용 공개 폼)
 */

/* ─── Link Types ─── */
const LINK_TYPES = [
  { id: 'android', title: 'Google Play', icon: <FaAndroid size={14} />, placeholder: 'https://play.google.com/store/apps/details?id=******' },
  { id: 'ios', title: 'iOS Appstore', icon: <FaApple size={14} />, placeholder: 'https://itunes.apple.com/us/app/******' },
  { id: 'steam', title: 'Steam link', icon: <FaSteam size={14} />, placeholder: 'https://store.steampowered.com/app/******' },
  { id: 'web', title: 'Main Webpage', icon: <Globe size={14} />, placeholder: 'https://example.com' },
] as const;

/* ─── Types ─── */
interface FormData {
  title: string;
  advertiserName: string;
  contactEmail: string;
  contactPhone: string;
  links: Record<string, string>;
  periodStart: string;
  periodEnd: string;
  budget: number;
  reviewVideos: boolean;
  regions: string[];
  languages: string[];
  gameIntroduction: string;
  gameDescription: string;
  talkingPoints: string[];
  comments: string;
}

interface FormErrors {
  [key: string]: string | undefined;
}

const INITIAL: FormData = {
  title: '',
  advertiserName: '',
  contactEmail: '',
  contactPhone: '',
  links: {},
  periodStart: '',
  periodEnd: '',
  budget: 0,
  reviewVideos: false,
  regions: [],
  languages: [],
  gameIntroduction: '',
  gameDescription: '',
  talkingPoints: [''],
  comments: '',
};

/* ─── Helpers ─── */
function validateEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/* ─── Component ─── */
function CampaignChecklist() {
  const [data, setData] = useState<FormData>(INITIAL);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = useCallback(<K extends keyof FormData>(key: K, value: FormData[K]) => {
    setData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }, []);

  const setLink = useCallback((id: string, value: string) => {
    setData((prev) => ({ ...prev, links: { ...prev.links, [id]: value.trim() } }));
    setErrors((prev) => ({ ...prev, links: undefined }));
  }, []);

  const addTalkingPoint = useCallback(() => {
    setData((prev) => {
      if (prev.talkingPoints.length >= 12) return prev;
      return { ...prev, talkingPoints: [...prev.talkingPoints, ''] };
    });
  }, []);

  const removeTalkingPoint = useCallback((idx: number) => {
    setData((prev) => {
      if (prev.talkingPoints.length <= 1) return prev;
      return { ...prev, talkingPoints: prev.talkingPoints.filter((_, i) => i !== idx) };
    });
  }, []);

  const setTalkingPoint = useCallback((idx: number, value: string) => {
    setData((prev) => {
      const pts = [...prev.talkingPoints];
      pts[idx] = value;
      return { ...prev, talkingPoints: pts };
    });
    setErrors((prev) => ({ ...prev, talkingPoints: undefined }));
  }, []);

  const validate = useCallback((): boolean => {
    const e: FormErrors = {};
    if (!data.title.trim()) e.title = 'Please input campaign name';
    if (!data.advertiserName.trim()) e.advertiserName = 'Please input advertiser name';
    if (!data.contactEmail.trim()) e.contactEmail = 'Please input contact email';
    else if (!validateEmail(data.contactEmail)) e.contactEmail = 'Incorrect email address';
    const hasLink = Object.values(data.links).some((v) => !!v);
    if (!hasLink) e.links = 'Please input some links';
    else {
      for (const lt of LINK_TYPES) {
        const v = data.links[lt.id];
        if (v && !/^https?:\/\//.test(v)) { e.links = `${lt.title} link is incorrect`; break; }
      }
    }
    if (!data.periodStart || !data.periodEnd) e.period = 'Please input proposed campaign period';
    if (!data.budget) e.budget = 'Please input budget';
    if (!data.gameIntroduction.trim()) e.gameIntroduction = 'Please input game introduction';
    if (!data.gameDescription.trim()) e.gameDescription = 'Please input game description';
    if (!data.talkingPoints.some((tp) => tp.trim())) e.talkingPoints = 'Please input talking points';
    setErrors(e);
    return Object.keys(e).length === 0;
  }, [data]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = {
        title: data.title,
        advertiserName: data.advertiserName,
        contactEmail: data.contactEmail,
        contactPhone: data.contactPhone || undefined,
        links: data.links,
        period: data.periodStart && data.periodEnd ? [data.periodStart, data.periodEnd] : undefined,
        budget: data.budget,
        reviewVideos: data.reviewVideos,
        regions: data.regions.length > 0 ? data.regions : undefined,
        languages: data.languages.length > 0 ? data.languages : undefined,
        gameIntroduction: data.gameIntroduction,
        gameDescription: data.gameDescription,
        talkingPoints: data.talkingPoints.filter((tp) => tp.trim()),
        comments: data.comments || undefined,
      };
      await (useNewInfluencerAuth()
        ? influencerApi.post('campaign-checklist/save', payload as unknown as Record<string, unknown>)
        : webApi.post('campaignChecklist/saveCampaignChecklist', payload as unknown as Record<string, unknown>));
      setSubmitted(true);
    } catch {
      // webApi handles errors
    } finally {
      setLoading(false);
    }
  }, [data, validate]);

  const toggleRegion = useCallback((value: string) => {
    setData((prev) => {
      const next = prev.regions.includes(value)
        ? prev.regions.filter((r) => r !== value)
        : [...prev.regions, value];
      return { ...prev, regions: next };
    });
  }, []);

  const toggleLanguage = useCallback((value: string) => {
    setData((prev) => {
      const next = prev.languages.includes(value)
        ? prev.languages.filter((l) => l !== value)
        : [...prev.languages, value];
      return { ...prev, languages: next };
    });
  }, []);

  if (submitted) {
    return (
      <div className="cl-page">
        <div className="cl-container">
          <h1 className="cl-title"><span>Campaign</span> Checklist</h1>
          <div className="cl-success">
            <div className="cl-success-icon">✓</div>
            <h3>Thank you for reaching out to us!</h3>
            <p>We will contact you back soon.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cl-page">
      <div className="cl-container">
        <h1 className="cl-title"><span>Campaign</span> Checklist</h1>

        <div className="cl-form">
          {/* 1. Campaign/Game Name */}
          <FormRow index={0} label="Campaign/Game Name" error={errors.title} required>
            <input
              className="cl-input" value={data.title}
              onChange={(e) => set('title', e.target.value)}
            />
          </FormRow>

          {/* 2. Advertiser name */}
          <FormRow index={1} label="Advertiser name" error={errors.advertiserName} required>
            <input
              className="cl-input" value={data.advertiserName}
              onChange={(e) => set('advertiserName', e.target.value)}
            />
          </FormRow>

          {/* 3. Contact email */}
          <FormRow index={2} label="Contact email" error={errors.contactEmail} required>
            <div className="cl-input-addon">
              <span className="cl-addon"><Mail size={14} /></span>
              <input
                type="email" className="cl-input" value={data.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
              />
            </div>
          </FormRow>

          {/* 4. Contact phone */}
          <FormRow index={3} label="Contact phone">
            <div className="cl-input-addon">
              <span className="cl-addon"><Phone size={14} /></span>
              <input
                className="cl-input" placeholder="(optional)" value={data.contactPhone}
                onChange={(e) => set('contactPhone', e.target.value)}
              />
            </div>
          </FormRow>

          {/* 5. Game/Product links */}
          <FormRow index={4} label="Game/Product links" error={errors.links} required>
            <div className="cl-links">
              {LINK_TYPES.map((lt) => (
                <div key={lt.id} className="cl-link-item">
                  <div className="cl-input-addon">
                    <span className="cl-addon" title={lt.title}>{lt.icon}</span>
                    <input
                      className="cl-input" placeholder={lt.placeholder}
                      value={data.links[lt.id] || ''}
                      onChange={(e) => setLink(lt.id, e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
          </FormRow>

          {/* 6. Proposed Campaign Period */}
          <FormRow index={5} label="Proposed Campaign Period" error={errors.period} required>
            <div className="cl-date-range">
              <input
                type="date" className="cl-input cl-date"
                value={data.periodStart}
                onChange={(e) => set('periodStart', e.target.value)}
              />
              <span className="cl-date-sep">~</span>
              <input
                type="date" className="cl-input cl-date"
                value={data.periodEnd}
                onChange={(e) => set('periodEnd', e.target.value)}
              />
            </div>
          </FormRow>

          {/* 7. Total Budget */}
          <FormRow index={6} label="Total Budget" error={errors.budget} required>
            <div className="cl-input-addon">
              <span className="cl-addon">$</span>
              <input
                type="number" className="cl-input" min={0}
                value={data.budget || ''}
                onChange={(e) => set('budget', Number(e.target.value))}
              />
            </div>
          </FormRow>

          {/* 8. Reviewing Videos */}
          <FormRow index={7} label="Reviewing Videos" comment="Would you prefer to review submitted videos prior to release?">
            <label className="cl-switch">
              <input
                type="checkbox" checked={data.reviewVideos}
                onChange={(e) => set('reviewVideos', e.target.checked)}
              />
              <span className="cl-switch-slider" />
              <span className="cl-switch-label">{data.reviewVideos ? 'Yes' : 'No'}</span>
            </label>
          </FormRow>

          {/* 9. Target Regions */}
          <FormRow index={8} label="Target Regions">
            <RegionSelect
              tree={regionsTree}
              selected={data.regions}
              onToggle={toggleRegion}
            />
          </FormRow>

          {/* 10. Target Languages */}
          <FormRow index={9} label="Target Languages">
            <LanguageSelect
              selected={data.languages}
              onToggle={toggleLanguage}
            />
          </FormRow>

          {/* 11. Game Introduction */}
          <FormRow index={10} label="Game Introduction" error={errors.gameIntroduction} required
            comment="Ex: Elsword is a free-to-play, 2.5D action MMORPG by KOG Games. It features both PvE and PvP real-time action gameplay.">
            <textarea
              className="cl-textarea" rows={4} value={data.gameIntroduction}
              onChange={(e) => set('gameIntroduction', e.target.value)}
            />
          </FormRow>

          {/* 12. Game Description */}
          <FormRow index={11} label="Game Description" error={errors.gameDescription} required
            comment="Ex: In Elsword, gamers can choose from ten specialized and customizable characters as they explore an immersive world...">
            <textarea
              className="cl-textarea" rows={8} value={data.gameDescription}
              onChange={(e) => set('gameDescription', e.target.value)}
            />
          </FormRow>

          {/* 13. Talking points */}
          <FormRow index={12} label="Talking points" error={errors.talkingPoints} required>
            <div className="cl-talking-points">
              {data.talkingPoints.map((tp, i) => (
                <div key={i} className="cl-tp-row">
                  <textarea
                    className="cl-textarea" rows={2} value={tp}
                    onChange={(e) => setTalkingPoint(i, e.target.value)}
                    placeholder={`Talking point ${i + 1}`}
                  />
                  {data.talkingPoints.length > 1 && (
                    <button type="button" className="cl-tp-remove" onClick={() => removeTalkingPoint(i)}>×</button>
                  )}
                </div>
              ))}
              {data.talkingPoints.length < 12 && (
                <button type="button" className="cl-tp-add" onClick={addTalkingPoint}>+ Add talking point</button>
              )}
            </div>
          </FormRow>

          {/* 14. Comments */}
          <FormRow index={13} label="Comments" comment="Please leave us any comments you might have.">
            <textarea
              className="cl-textarea" rows={4} value={data.comments}
              onChange={(e) => set('comments', e.target.value)}
            />
          </FormRow>

          {/* Submit */}
          <div className="cl-submit-wrapper">
            <button
              className="cl-submit-btn"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Apply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── FormRow ─── */
function FormRow({ index, label, comment, error, required, children }: {
  index: number;
  label: string;
  comment?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`cl-row ${index % 2 ? 'cl-row-odd' : 'cl-row-even'}`}>
      <div className="cl-label">
        <div className="cl-label-title">
          {label}
          {required && <span className="cl-required">*</span>}
        </div>
        {comment && <div className="cl-label-comment">{comment}</div>}
      </div>
      <div className="cl-field">
        {children}
        {error && <div className="cl-error">{error}</div>}
      </div>
    </div>
  );
}

/* ─── RegionSelect (tree-style checkboxes) ─── */
function RegionSelect({ tree, selected, onToggle }: {
  tree: RegionNode[];
  selected: string[];
  onToggle: (value: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (val: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val); else next.add(val);
      return next;
    });
  };

  const renderNodes = (nodes: RegionNode[], depth = 0): ReactNode => {
    return nodes.map((n) => {
      const matchesSearch = !search || n.title.toLowerCase().includes(search.toLowerCase());
      const hasChildren = n.children && n.children.length > 0;
      const childrenMatch = hasChildren && n.children!.some((c) =>
        c.title.toLowerCase().includes(search.toLowerCase()) ||
        (c.children && c.children.some((cc) => cc.title.toLowerCase().includes(search.toLowerCase())))
      );

      if (search && !matchesSearch && !childrenMatch) return null;
      const isExpanded = expanded.has(n.value) || !!search;

      return (
        <div key={n.value} style={{ paddingLeft: depth * 16 }}>
          <label className="cl-region-item">
            <input
              type="checkbox"
              checked={selected.includes(n.value)}
              onChange={() => onToggle(n.value)}
            />
            {hasChildren && (
              <span className="cl-region-toggle" onClick={(e) => { e.preventDefault(); toggle(n.value); }}>
                {isExpanded ? '▼' : '▶'}
              </span>
            )}
            <span>{n.title}</span>
          </label>
          {hasChildren && isExpanded && renderNodes(n.children!, depth + 1)}
        </div>
      );
    });
  };

  return (
    <div className="cl-multi-select">
      <input
        className="cl-input cl-multi-search"
        placeholder="Search regions..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {selected.length > 0 && (
        <div className="cl-selected-tags">
          {selected.map((v) => (
            <span key={v} className="cl-tag">
              {v.split('-').map((w) => w[0]?.toUpperCase() + w.slice(1)).join(' ')}
              <button type="button" onClick={() => onToggle(v)}>×</button>
            </span>
          ))}
        </div>
      )}
      <div className="cl-region-tree">{renderNodes(tree)}</div>
    </div>
  );
}

/* ─── LanguageSelect ─── */
function LanguageSelect({ selected, onToggle }: {
  selected: string[];
  onToggle: (value: string) => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return languages;
    const s = search.toLowerCase();
    return languages.filter((l) => l.text.toLowerCase().includes(s) || l.value.includes(s));
  }, [search]);

  return (
    <div className="cl-multi-select">
      <input
        className="cl-input cl-multi-search"
        placeholder="Search languages..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {selected.length > 0 && (
        <div className="cl-selected-tags">
          {selected.map((v) => {
            const lang = languages.find((l) => l.value === v);
            return (
              <span key={v} className="cl-tag">
                {lang?.text || v}
                <button type="button" onClick={() => onToggle(v)}>×</button>
              </span>
            );
          })}
        </div>
      )}
      <div className="cl-lang-list">
        {filtered.map((l) => (
          <label key={l.value} className="cl-lang-item">
            <input
              type="checkbox"
              checked={selected.includes(l.value)}
              onChange={() => onToggle(l.value)}
            />
            <span>{l.text}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default CampaignChecklist;
